import { AiAssistant, AiLoader } from '@/assets/icons/ai';
import { CopyFromModal } from '@/components/ats';
import {
  Box,
  Button,
  Divider,
  Flex,
  Icon,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import { Star06 } from '@kula-ai/icons/outline/Star06';
import { Edit05 } from '@kula-ai/icons/dual-tone/Edit05';
import { atsJobsApiActions } from '@/store/atsJobs';
import { useDispatch } from 'react-redux';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';

export const AiPrompt = ({
  editorConfig = {},
  setEditorConfig = () => {},
  promptValue = '',
  setPromptValue = () => {},
  onSubmit = () => {},
  uiConfig = {},
}) => {
  const {
    heading = 'Ask AI',
    headingStyles = {},
    subHeading = `AI uses job fit criteria to craft a job description tailored to the needs of your company`,
    subHeadingStyles = {},
    textAreaStyles = {},
    placeholder = 'Write any specific instructions to help the AI generate the job description better',
    loadingText = 'Generating JD',
    submitButtonText = 'Generate JD',
    copyFromText = 'Copy from another job',
    copyFromOnClick = () => {},
    manualDefaultContent = `<h3><strong>About Job</strong></h3><p></p><p></p><h3><strong>Skills &amp; Qualification</strong></h3><ul><li><p></p></li><li><p></p></li><li><p></p><p></p></li></ul><h3><strong>Responsibilities</strong></h3><ul><li><p></p></li><li><p></p></li><li><p></p></li></ul>`,
  } = uiConfig;

  const modalActions = useDisclosure();
  const { jobPermissions } = useAtsPermissions();

  const { isAiLoading } = editorConfig;
  const dispatch = useDispatch();

  const sectionOne = () => {
    return (
      <>
        <Icon as={AiAssistant} w="revert-layer" h="revert-layer" />
        <Text
          {...headingStyles}
          size="xxl-regular"
          style={{
            background:
              '-webkit-linear-gradient(91.04deg, #7CD0FF 5.69%, #6249F9 88.5%)',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
          }}
        >
          {heading}
        </Text>
      </>
    );
  };

  const generateActions = () => {
    return (
      <>
        <Text
          p="5px 0"
          size="sm-medium"
          textAlign="center"
          {...subHeadingStyles}
        >
          {subHeading}
        </Text>
        <Box
          border="1px solid transparent"
          background="linear-gradient( #fff, #fff) padding-box,linear-gradient(180deg, #8A90FF 0%, #B15FDA 100%)"
          boxShadow="0px 1px 2px 0px #1018280D"
          borderRadius="sm"
          w="500px"
        >
          <Textarea
            borderRadius="8px"
            border="none"
            onChange={(event) => setPromptValue(event.target.value)}
            placeholder={placeholder}
            isDisabled={!jobPermissions?.update}
            _placeholder={{
              opacity: 1,
              fontSize: '12px',
              fontWeight: '400',
              color: 'secondary-700',
            }}
            _focus={{
              border: 'none',
            }}
            _hover={{
              border: 'none',
            }}
            {...textAreaStyles}
          />
        </Box>
        <Button
          loadingText={loadingText}
          isDisabled={!jobPermissions?.update}
          isLoading={isAiLoading}
          variant="primary"
          onClick={onSubmit}
          w="100%"
          m="0 3%"
          data-test-id="generate-jd-button"
        >
          <Icon as={Star06} mr="1em" fill="base-white" />
          {submitButtonText}
        </Button>
      </>
    );
  };

  const otherActions = () => {
    return (
      <>
        <Flex
          alignItems="center"
          justifyContent="center"
          position="relative"
          h="50px"
          w="100%"
          maxW="800px"
          m="1em 0"
        >
          <Divider w="30%" orientation="horizontal" borderColor="gray.300" />
          <Text px="4">OR</Text>
          <Divider w="30%" orientation="horizontal" borderColor="gray.300" />
        </Flex>
        <Flex gap="2em" m="1em 0" justifyContent="center">
          <Flex
            gap=".5em"
            alignItems="center"
            cursor={
              isAiLoading || !jobPermissions?.update ? 'not-allowed' : 'pointer'
            }
            opacity={isAiLoading || !jobPermissions?.update ? 0.5 : 1}
            onClick={() => {
              if (jobPermissions?.update) {
                setEditorConfig((prev) => ({
                  ...prev,
                  isAi: false,
                  replaceContent: false,
                  content: manualDefaultContent,
                  show: true,
                }));
              }
            }}
          >
            <Icon as={Edit05} color="ats-primary" />
            <Text size="sm-medium" color="ats-primary">
              Add manually
            </Text>
          </Flex>
          <CopyFromModal
            isDisabled={isAiLoading || !jobPermissions?.update}
            label={copyFromText}
            modalConfig={{
              modalAction: (content, onClose) => {
                setEditorConfig((prev) => ({
                  ...prev,
                  content,
                  show: true,
                  replaceContent: true,
                }));
                onClose();
              },
            }}
            modalActions={modalActions}
            type="jd"
            apiCall={() =>
              dispatch(
                atsJobsApiActions('getAllJobs', {
                  requestConfig: {
                    params: {
                      view: 'job_description',
                      limit: 100,
                      'sort_by[open_at]': 'DESC',
                    },
                  },
                })
              )
            }
            onClick={copyFromOnClick}
          />
        </Flex>
      </>
    );
  };

  const renderDefault = () => {
    return (
      <>
        <Flex
          flexDir="column"
          alignItems="center"
          justifyContent="center"
          gap="10px"
          p="20px 15px"
        >
          {sectionOne()}
          <Flex
            minH="20vh"
            alignItems="center"
            justifyContent="center"
            flexDir="column"
            gap="1.375em"
          >
            {generateActions()}{' '}
          </Flex>
        </Flex>
        {otherActions()}
      </>
    );
  };

  return (
    <>
      {isAiLoading ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap=".5em"
        >
          <Icon as={AiLoader} w="100px" h="100px" />
          <Text
            size="xxl-regular"
            style={{
              background:
                '-webkit-linear-gradient(91.04deg, #7CD0FF 5.69%, #6249F9 88.5%)',
              WebkitBackgroundClip: 'text',
              color: 'transparent',
            }}
          >
            AI is Writing
          </Text>
        </Flex>
      ) : (
        renderDefault()
      )}
    </>
  );
};
